import {
  AMOUNT_DECIMAL_SEPARATOR as DOT,
  AMOUNT_GROUP_SEPARATOR as COMMA,
  LAKH_GROUP_DIGITS,
  MAX_AMOUNT_DECIMALS,
  THOUSANDS_GROUP_DIGITS,
  groupAmountDigits,
} from './agreedMoney';

export interface AmountEdit {
  text: string;
  caret: number;
}

/** A rejected edit leaves the field as it was and shows AMOUNT_PASTE_ERROR. */
export type AmountEditResult = { kind: 'edit'; edit: AmountEdit } | { kind: 'rejected' };

/** What the browser holds right after a keystroke, before re-formatting. */
export interface AmountKeystroke {
  previous: string;
  value: string;
  caret: number;
  inputType?: string;
  currency: string;
}

export interface AmountPaste {
  current: string;
  selectionStart: number;
  selectionEnd: number;
  pasted: string;
  currency: string;
}

export const AMOUNT_PASTE_ERROR = "Couldn't read that amount. Paste a number like 150000, 150,000 or 1,50,000.00.";

const DELETE_BACKWARD_INPUT = 'deleteContentBackward';
const DELETE_FORWARD_INPUT = 'deleteContentForward';
const ZERO = '0';
const DIGIT = /^\d$/;
const DIGITS_ONLY = /^\d+$/;
const LEADING_ZEROS = /^0+/;
const KEYSTROKE_LENGTH = 1;
const REGEXP_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

// Only the row's own currency may decorate a pasted amount, so "USD 500" never lands in a rupee field as 500.
const CURRENCY_SYMBOLS: Readonly<Record<string, readonly string[]>> = {
  INR: ['₹', 'Rs.', 'Rs'],
  USD: ['$'],
};
// Indian invoices close a whole-rupee figure with "/-", as in "1,500/-".
const TRAILING_SLASH_DASH = /\s*\/-$/;

const DOT_PATTERN = escapeRegExp(DOT);
const COMMA_PATTERN = escapeRegExp(COMMA);
const PLAIN_WHOLE = '\\d+';
const THOUSANDS_WHOLE = `\\d{1,${THOUSANDS_GROUP_DIGITS}}(?:${COMMA_PATTERN}\\d{${THOUSANDS_GROUP_DIGITS}})+`;
const LAKH_WHOLE = `\\d{1,${LAKH_GROUP_DIGITS}}(?:${COMMA_PATTERN}\\d{${LAKH_GROUP_DIGITS}})*${COMMA_PATTERN}\\d{${THOUSANDS_GROUP_DIGITS}}`;
const FRACTION = `(?:${DOT_PATTERN}\\d{1,${MAX_AMOUNT_DECIMALS}})?`;
const READABLE_AMOUNT = new RegExp(`^(?:${PLAIN_WHOLE}|${THOUSANDS_WHOLE}|${LAKH_WHOLE})${FRACTION}$`);
const PLAIN_AMOUNT = new RegExp(`^\\d+(?:${DOT_PATTERN}\\d{0,${MAX_AMOUNT_DECIMALS}})?$`);

/** Field text plus the span [insertStart, insertEnd) the user just typed or pasted; existing text wins conflicts. */
interface AmountDraft {
  value: string;
  caret: number;
  insertStart: number;
  insertEnd: number;
}

/** Text that arrived in one go (paste, drop, autofill) and the field text around it. */
interface AmountChunk {
  before: string;
  inserted: string;
  after: string;
  currency: string;
}

interface CurrencyMarkerPatterns {
  leading: RegExp;
  trailing: RegExp;
}

interface CharAt {
  char: string;
  index: number;
}

interface SignificantParts {
  whole: CharAt[];
  dot: CharAt | null;
  fraction: CharAt[];
}

interface WholeDigits {
  digits: string;
  removed: number;
  added: number;
}

const REJECTED: AmountEditResult = { kind: 'rejected' };

export function maskAmountKeystroke(k: AmountKeystroke): AmountEditResult {
  const { value, caret } = widenCommaDeletion(k);
  const insertStart = insertionStart(k.previous, value, caret);
  const draft: AmountDraft = { value, caret, insertStart, insertEnd: caret };
  if (isMixedChunk(value.slice(insertStart, caret))) return readTypedChunk(draft, k.currency);
  if (addsNothingSignificant(k.previous, value, insertStart, caret)) {
    return accepted({ text: k.previous, caret: insertStart });
  }
  return accepted(normaliseAmount(draft, k.currency));
}

export function maskAmountPaste(p: AmountPaste): AmountEditResult {
  return readAmountChunk({
    before: p.current.slice(0, p.selectionStart),
    inserted: p.pasted,
    after: p.current.slice(p.selectionEnd),
    currency: p.currency,
  });
}

/** The browser's inputType ("insertText", "deleteContentBackward", ...), or undefined for a synthetic change such as autofill. */
export function inputTypeOf(event: Event): string | undefined {
  if ('inputType' in event && typeof event.inputType === 'string') return event.inputType;
  return undefined;
}

/** Masked text to a number: "1,50,000.5" -> 150000.5, "" -> 0. */
export function amountFromInput(text: string): number {
  return Number(stripGroupSeparators(text));
}

/** Re-groups already-masked text for another currency (used when the dialog's currency changes). */
export function regroupAmountText(text: string, currency: string): string {
  const end = text.length;
  return normaliseAmount({ value: text, caret: end, insertStart: end, insertEnd: end }, currency).text;
}

function accepted(edit: AmountEdit): AmountEditResult {
  return { kind: 'edit', edit };
}

function escapeRegExp(text: string): string {
  return text.replace(REGEXP_SPECIAL_CHARS, '\\$&');
}

function isDigit(char: string): boolean {
  return DIGIT.test(char);
}

function isSignificant(char: string): boolean {
  return isDigit(char) || char === DOT;
}

function stripGroupSeparators(text: string): string {
  return text.replaceAll(COMMA, '');
}

function removesOnlyCommaAtCaret(k: AmountKeystroke): boolean {
  if (k.previous.charAt(k.caret) !== COMMA) return false;
  return k.value === `${k.previous.slice(0, k.caret)}${k.previous.slice(k.caret + 1)}`;
}

function digitBesideDeletedComma(k: AmountKeystroke): number | null {
  if (!removesOnlyCommaAtCaret(k)) return null;
  if (k.inputType === DELETE_BACKWARD_INPUT) return k.caret - 1;
  if (k.inputType === DELETE_FORWARD_INPUT) return k.caret;
  return null;
}

/** Deleting just a comma would be undone by regrouping, so the delete also takes the digit beside it. */
function widenCommaDeletion(k: AmountKeystroke): { value: string; caret: number } {
  const index = digitBesideDeletedComma(k);
  if (index === null || !isDigit(k.value.charAt(index))) return { value: k.value, caret: k.caret };
  const value = `${k.value.slice(0, index)}${k.value.slice(index + 1)}`;
  return { value, caret: Math.min(index, k.caret) };
}

/** Where the typed text starts: the previous text is the value minus [start, caret), give or take a replaced range. */
function insertionStart(previous: string, value: string, caret: number): number {
  const unchangedTail = value.length - caret;
  const limit = Math.max(0, Math.min(caret, previous.length - unchangedTail));
  let start = 0;
  while (start < limit && previous.charAt(start) === value.charAt(start)) start += 1;
  return start;
}

// A single key or an all-digit run (IME) can only mean those digits; anything else that arrives at once could be a misread amount.
function isMixedChunk(inserted: string): boolean {
  return inserted.length > KEYSTROKE_LENGTH && !DIGITS_ONLY.test(inserted);
}

/** Read like a paste; failing that, keep a field that now holds one readable amount, as an undo or redo restores. */
function readTypedChunk(draft: AmountDraft, currency: string): AmountEditResult {
  const asPaste = readAmountChunk({
    before: draft.value.slice(0, draft.insertStart),
    inserted: draft.value.slice(draft.insertStart, draft.insertEnd),
    after: draft.value.slice(draft.insertEnd),
    currency,
  });
  if (asPaste.kind === 'edit') return asPaste;
  if (!READABLE_AMOUNT.test(draft.value)) return REJECTED;
  return accepted(normaliseAmount(draft, currency));
}

/** Accepted only when the chunk is one readable amount and, with the field text around it, still reads as one plain amount. */
function readAmountChunk(chunk: AmountChunk): AmountEditResult {
  const digits = readPastedAmount(chunk.inserted, chunk.currency);
  if (digits === null) return REJECTED;
  const before = stripGroupSeparators(chunk.before);
  const value = `${before}${digits}${stripGroupSeparators(chunk.after)}`;
  if (!PLAIN_AMOUNT.test(value)) return REJECTED;
  const insertEnd = before.length + digits.length;
  return accepted(normaliseAmount({ value, caret: insertEnd, insertStart: before.length, insertEnd }, chunk.currency));
}

// Users never type grouping, so a typed comma is a no-op like a letter; a decimal-comma mobile keypad is a known limitation.
function addsNothingSignificant(previous: string, value: string, insertStart: number, caret: number): boolean {
  const inserted = value.slice(insertStart, caret);
  if (!inserted) return false;
  const isPureInsertion = previous === `${value.slice(0, insertStart)}${value.slice(caret)}`;
  return isPureInsertion && !inserted.split('').some(isSignificant);
}

function isInserted(draft: AmountDraft, index: number): boolean {
  return index >= draft.insertStart && index < draft.insertEnd;
}

function charsOf(text: string): CharAt[] {
  return text.split('').map((char, index) => ({ char, index }));
}

/** A dot typed in front of more existing digits than fit as decimals is dropped, like a third decimal digit. */
function leavesTooManyDecimals(draft: AmountDraft, dotIndex: number): boolean {
  const existingAfter = charsOf(draft.value).filter((c) => c.index > dotIndex && isDigit(c.char) && !isInserted(draft, c.index));
  return existingAfter.length > MAX_AMOUNT_DECIMALS;
}

function keptDotIndex(draft: AmountDraft): number | null {
  const dots = charsOf(draft.value)
    .filter((c) => c.char === DOT)
    .map((c) => c.index);
  const existing = dots.find((index) => !isInserted(draft, index));
  if (existing !== undefined) return existing;
  const typed = dots[0];
  if (typed === undefined || leavesTooManyDecimals(draft, typed)) return null;
  return typed;
}

/** Keeps the existing decimals first, then as many typed ones as still fit. */
function keepFractionDigits(fraction: CharAt[], draft: AmountDraft): CharAt[] {
  const existing = fraction.filter((c) => !isInserted(draft, c.index));
  const room = Math.max(0, MAX_AMOUNT_DECIMALS - existing.length);
  const typed = fraction.filter((c) => isInserted(draft, c.index)).slice(0, room);
  return fraction.filter((c) => !isInserted(draft, c.index) || typed.includes(c)).slice(0, MAX_AMOUNT_DECIMALS);
}

function splitSignificant(draft: AmountDraft): SignificantParts {
  const digits = charsOf(draft.value).filter((c) => isDigit(c.char));
  const dotIndex = keptDotIndex(draft);
  if (dotIndex === null) return { whole: digits, dot: null, fraction: [] };
  return {
    whole: digits.filter((c) => c.index < dotIndex),
    dot: { char: DOT, index: dotIndex },
    fraction: keepFractionDigits(
      digits.filter((c) => c.index > dotIndex),
      draft,
    ),
  };
}

function leadingZeroCount(digits: string): number {
  return LEADING_ZEROS.exec(digits)?.[0].length ?? 0;
}

/** Typing into or right after the leading zeros tidies them; zeros a deletion exposes stay, so the digit can be typed back. */
function typesAtLeadingZeros(whole: CharAt[], draft: AmountDraft): boolean {
  if (draft.insertEnd <= draft.insertStart) return false;
  const lastLeadingZero = whole[leadingZeroCount(joinChars(whole)) - 1];
  if (!lastLeadingZero) return false;
  return draft.insertStart <= lastLeadingZero.index + 1;
}

/** A bare dot gets a "0" in front. */
function padBareDot(digits: string, hasDot: boolean): WholeDigits {
  if (!digits && hasDot) return { digits: ZERO, removed: 0, added: 1 };
  return { digits, removed: 0, added: 0 };
}

/** "007" -> "7" and "00" -> "0". Reports the shift so the caret can follow. */
function collapseLeadingZeros(digits: string, hasDot: boolean): WholeDigits {
  const zeros = leadingZeroCount(digits);
  const rest = digits.slice(zeros);
  if (rest) return { digits: rest, removed: zeros, added: 0 };
  if (zeros > 0) return { digits: ZERO, removed: zeros - 1, added: 0 };
  return padBareDot(digits, hasDot);
}

function wholeDigitsOf(parts: SignificantParts, draft: AmountDraft): WholeDigits {
  const digits = joinChars(parts.whole);
  const hasDot = parts.dot !== null;
  if (typesAtLeadingZeros(parts.whole, draft)) return collapseLeadingZeros(digits, hasDot);
  return padBareDot(digits, hasDot);
}

function joinChars(chars: CharAt[]): string {
  return chars.map((c) => c.char).join('');
}

/** The caret index right after the count-th digit or dot of the text. */
function caretAfterSignificant(text: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (isSignificant(text.charAt(index))) seen += 1;
    if (seen === count) return index + 1;
  }
  return text.length;
}

function normaliseAmount(draft: AmountDraft, currency: string): AmountEdit {
  const parts = splitSignificant(draft);
  const kept = parts.dot ? [...parts.whole, parts.dot, ...parts.fraction] : parts.whole;
  const keptBeforeCaret = kept.filter((c) => c.index < draft.caret).length;
  const whole = wholeDigitsOf(parts, draft);
  const decimals = parts.dot ? `${DOT}${joinChars(parts.fraction)}` : '';
  const text = `${groupAmountDigits(whole.digits, currency)}${decimals}`;
  const significantBeforeCaret = Math.max(0, keptBeforeCaret - whole.removed) + whole.added;
  return { text, caret: caretAfterSignificant(text, significantBeforeCaret) };
}

/** The row currency's own code and symbols; "Rs." comes before "Rs" so its dot is stripped too. */
function currencyMarkerPatterns(currency: string): CurrencyMarkerPatterns {
  const code = currency.toUpperCase();
  const markers = [code, ...(CURRENCY_SYMBOLS[code] ?? [])].map(escapeRegExp).join('|');
  return {
    leading: new RegExp(`^(?:${markers})\\s*`, 'i'),
    trailing: new RegExp(`\\s*(?:${markers})$`, 'i'),
  };
}

/** At most one marker comes off, so "INR 500 INR" stays unreadable. */
function stripOneCurrencyMarker(text: string, patterns: CurrencyMarkerPatterns): string {
  const withoutLeading = text.replace(patterns.leading, '');
  if (withoutLeading !== text) return withoutLeading;
  return text.replace(patterns.trailing, '');
}

/** Drops the whitespace, the row currency's marker and the "/-" a copied amount often carries. */
function stripAmountDecorations(pasted: string, currency: string): string {
  const withoutSlashDash = pasted.trim().replace(TRAILING_SLASH_DASH, '');
  return stripOneCurrencyMarker(withoutSlashDash, currencyMarkerPatterns(currency)).trim();
}

/** The pasted amount as plain digits and dot, or null when its shape is not one we can read for sure. */
function readPastedAmount(pasted: string, currency: string): string | null {
  const bare = stripAmountDecorations(pasted, currency);
  if (!READABLE_AMOUNT.test(bare)) return null;
  return stripGroupSeparators(bare);
}
